import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Loader2, Settings, Globe, X, Send, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { API, TickerCard, TickerLogo } from "@/App";
import WesEngineBadge from "@/components/WesEngineBadge";
// Iter109 — Market Scanner → Strategy Scanner bridge.
import { useMarketScannerBridge } from "@/contexts/MarketScannerBridgeContext";
// Iter121.6 — Multi-watchlist picker + manage modal + hook so the
// operator can seed the Symbols (override) field from any curated
// list without leaving the scanner.
import { WatchlistPicker, ManageWatchlistsModal, useActiveWatchlist } from "@/components/WatchlistPicker";
import { useI18n } from "@/i18n";

export default function MarketScanner() {
  const { t } = useI18n();
  const navigate = useNavigate();
  // Iter109 — bridge publisher (in-memory only, no persistence).
  const { publish: publishBridge } = useMarketScannerBridge();
  // Iter110 — operator pick-list.  Pure in-memory `Set` of
  // pinned ticker symbols.  When `pinMode` is ON, the Push
  // button + bridge publish use ONLY the pinned rows instead of
  // the whole post-filter view.  Pin state never persists.
  const [pinnedSymbols, setPinnedSymbols] = useState(new Set());
  const [pinMode, setPinMode] = useState(false);
  // Iter121.6 — Multi-watchlist state. `showManageWatchlists` gates
  // the CRUD modal. `seedingFromList` shows a spinner on the seed
  // button while we fetch the active list items.
  const {
    watchlists,
    activeId: activeWatchlistId,
    activeWatchlist,
    switchActive: switchActiveWatchlist,
    refetch: refetchWatchlists,
  } = useActiveWatchlist();
  const [showManageWatchlists, setShowManageWatchlists] = useState(false);
  const [seedingFromList, setSeedingFromList] = useState(false);
  const [seedError, setSeedError] = useState(null);
  const togglePin = (sym) => {
    const s = String(sym || "").trim().toUpperCase();
    if (!s) return;
    setPinnedSymbols(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  };
  const clearPins = () => setPinnedSymbols(new Set());
  const [scan, setScan] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [filters, setFilters] = useState({ price_min: 10, price_max: 200, min_avg_volume: 500000, min_ivr: null, min_wes_p: null, min_wes_c: null, min_csp_roi: null, symbols: "" });
  const [showFilters, setShowFilters] = useState(true);
  // Client-side result filters
  const [fMinRoi, setFMinRoi] = useState("");
  const [fMinWesP, setFMinWesP] = useState("");
  const [fOnlyValid, setFOnlyValid] = useState(true);
  const [sortCol, setSortCol] = useState("csp_roi");
  const [sortDir, setSortDir] = useState("desc");
  const [quickTicker, setQuickTicker] = useState(null);
  const [quickData, setQuickData] = useState(null);
  const [quickLoading, setQuickLoading] = useState(false);
  // Iter115.2 — live re-analysis: poll `/api/scan/live` every 20 s
  // while the Quick Analyze modal is open so Credit / Bid-Ask /
  // Δ / OI / ROI reflect the current market.  Read-only endpoint
  // (no history write).  `quickRefreshedAt` drives the "Xs ago"
  // badge; `quickRefreshing` drives a subtle pulsing indicator
  // (no full-page spinner flash so the operator can keep reading
  // the card while it breathes).
  const [quickRefreshedAt, setQuickRefreshedAt] = useState(null);
  const [quickRefreshing, setQuickRefreshing] = useState(false);
  // Iter115.5 — when the backend rejects a Start with "already_running",
  // we stash the rejection here so the UI can pop a confirm modal
  // instead of silently swallowing the response.
  const [alreadyRunningInfo, setAlreadyRunningInfo] = useState(null);
  // Iter115.5 — "Why was X dropped?" diagnostic state.
  const [diagSymbol, setDiagSymbol] = useState("");
  const [diagResult, setDiagResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiagnostic = async () => {
    const s = diagSymbol.trim().toUpperCase();
    if (!s) return;
    setDiagLoading(true);
    setDiagResult(null);
    try {
      const r = await axios.get(`${API}/market-scanner/diagnose/${encodeURIComponent(s)}`);
      setDiagResult(r.data);
    } catch (e) {
      setDiagResult({ verdict: "error", reason: e.response?.data?.detail || "Lookup failed" });
    }
    setDiagLoading(false);
  };
  const sendDiagToOverride = () => {
    if (!diagResult?.symbol) return;
    const cur = (filters.symbols || "").trim();
    const next = cur ? `${cur}, ${diagResult.symbol}` : diagResult.symbol;
    setFilters({ ...filters, symbols: next });
    setDiagResult(null);
    setDiagSymbol("");
  };
  // Iter121.6 — Seed the "Symbols (override)" field with the current
  // active watchlist's items. Called from the picker's "Use active
  // watchlist" button. Fetches items server-side so the override
  // reflects whatever the operator can see (shared or personal).
  // Non-destructive: it REPLACES the override text (feedback is
  // immediate — the field visibly fills up), never merges silently.
  const seedFromActiveWatchlist = async () => {
    setSeedError(null);
    if (!activeWatchlistId) {
      setSeedError("No active watchlist");
      return;
    }
    setSeedingFromList(true);
    try {
      const r = await axios.get(`${API}/watchlist`, { params: { watchlist_id: activeWatchlistId } });
      const items = Array.isArray(r.data) ? r.data : [];
      if (items.length === 0) {
        setSeedError("Active watchlist is empty");
        return;
      }
      const syms = items.map((it) => (it.symbol || "").trim().toUpperCase()).filter(Boolean);
      setFilters((prev) => ({ ...prev, symbols: syms.join(", ") }));
    } catch (err) {
      setSeedError(err?.response?.data?.detail || "Failed to load watchlist");
    } finally {
      setSeedingFromList(false);
    }
  };
  const pollRef = useRef(null);

  useEffect(() => { fetchStatus(); fetchResults(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  const fetchStatus = async () => { try { const r = await axios.get(`${API}/market-scanner/status`); setStatus(r.data); if (r.data.status === "running") startPolling(); } catch {} setLoading(false); };
  const fetchResults = async () => {
    try {
      const r = await axios.get(`${API}/market-scanner/results`);
      if (r.data.status !== "none") {
        setScan(r.data);
        // Iter109 — publish to in-memory bridge for the Strategy
        // Scanner Pull link + Auto-chip.  No persistence, no
        // ranking, no auto-execute — just propagates the symbol
        // list of opportunities the operator already analysed.
        const symbols = (r.data.results || [])
          .filter(x => x && x.symbol && x.recommendation === "OK")
          .map(x => x.symbol);
        if (symbols.length > 0) {
          publishBridge({
            symbols,
            completedAt: r.data.completed_at || new Date().toISOString(),
            opportunities: r.data.opportunities ?? symbols.length,
            universe: r.data.universe_size ?? null,
            scanId: r.data.scan_id || null,
          });
        }
      }
    } catch {}
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/market-scanner/status`);
        setStatus(r.data);
        if (r.data.status !== "running") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchResults();
        }
      } catch {}
    }, 5000);
  };

  const startScan = async (opts = {}) => {
    setStarting(true);
    try {
      const payload = { ...filters };
      // Iter115.3 — symbols override.  Parse the comma/space/newline-
      // separated string into a clean uppercase ticker list; if any
      // are present, the backend will skip the SP500+R2K universe
      // AND the price/volume pre-filter and analyse only these.
      if (typeof payload.symbols === "string") {
        const syms = payload.symbols
          .split(/[\s,;\n]+/)
          .map(s => s.trim().toUpperCase())
          .filter(Boolean);
        if (syms.length > 0) {
          payload.symbols = syms;
        } else {
          delete payload.symbols;
        }
      }
      // Convert empty strings to null
      Object.keys(payload).forEach(k => { if (payload[k] === "" || payload[k] === null) delete payload[k]; });
      const r = await axios.post(`${API}/market-scanner/start`, payload);
      // Iter115.5 — explicit handling for `already_running`.  Previous
      // versions silently swallowed this response so an operator who
      // typed symbols and pressed Start would see nothing happen.
      // Now we either auto-replace (when called with `opts.replace`)
      // or surface an explicit confirm modal.
      if (r.data?.status === "already_running") {
        if (opts.replace) {
          // Cancel the running scan, then immediately retry.
          await axios.post(`${API}/market-scanner/cancel`).catch(() => {});
          const r2 = await axios.post(`${API}/market-scanner/start`, payload);
          setStatus({ ...r2.data, status: r2.data.status === "started" ? "running" : r2.data.status, progress_pct: 0 });
          if (r2.data?.status === "started") startPolling();
        } else {
          // Pop the confirm modal — keep the user's payload pinned
          // in state so the replace flow uses the same symbols.
          setAlreadyRunningInfo({
            started_at: r.data.started_at,
            payload_summary: payload.symbols
              ? `Override (${payload.symbols.length} symbol${payload.symbols.length === 1 ? "" : "s"})`
              : "Full universe sweep",
          });
        }
      } else {
        setStatus({ ...r.data, status: r.data.status === "started" ? "running" : r.data.status, progress_pct: 0 });
        if (r.data?.status === "started") startPolling();
      }
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    setStarting(false);
  };

  const cancelScan = async () => { try { await axios.post(`${API}/market-scanner/cancel`); fetchStatus(); } catch {} };

  const quickAnalyze = async (symbol) => {
    setQuickTicker(symbol);
    setQuickLoading(true);
    setQuickData(null);
    setQuickRefreshedAt(null);
    try {
      const r = await axios.post(`${API}/scan/live`, { symbols: [symbol] });
      const res = r.data?.results?.[0];
      if (res) setQuickData(res);
      setQuickRefreshedAt(new Date().toISOString());
    } catch {}
    setQuickLoading(false);
  };

  // Iter115.2 — silent re-analysis (no spinner flash).  Used by
  // the 20-second auto-refresh interval and the manual ↻ button
  // inside the modal header.  Updates `quickData` in place so
  // Credit / Bid-Ask / Δ / OI / ROI follow the market.
  const quickRefreshAnalysis = async (symbol) => {
    if (!symbol) return;
    setQuickRefreshing(true);
    try {
      const r = await axios.post(`${API}/scan/live`, { symbols: [symbol] });
      const res = r.data?.results?.[0];
      if (res) setQuickData(res);
      setQuickRefreshedAt(new Date().toISOString());
    } catch {}
    setQuickRefreshing(false);
  };

  // Iter115.2 — 20-second auto-refresh effect.  Tears down on
  // modal close (when `quickTicker` flips to null) so we never
  // poll for a symbol the operator can no longer see.
  useEffect(() => {
    if (!quickTicker) return undefined;
    const iv = setInterval(() => {
      quickRefreshAnalysis(quickTicker);
    }, 20_000);
    return () => clearInterval(iv);
  }, [quickTicker]);

  // Client-side filtering and sorting
  const results = scan?.results || [];
  const filtered = results.filter(r => {
    if (fOnlyValid && r.recommendation !== "OK") return false;
    if (fMinRoi && (r.csp_75d?.roi || 0) < parseFloat(fMinRoi)) return false;
    if (fMinWesP && (r.wes_hourly?.wes_p || 0) < parseFloat(fMinWesP)) return false;
    return true;
  });

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const getVal = (r, col) => {
    switch(col) {
      case "symbol": return r.symbol || "";
      case "company": return r.company || "";
      case "spot": return r.spot || 0;
      case "ivr": return r.ivr ?? -1;
      case "csp_roi": return r.csp_75d?.roi || 0;
      case "winner": return (r.winner === "ATM_CSP_ITM_CC" ? r.scenario_b : r.scenario_a)?.income_pct || 0;
      case "wes_p": return r.wes_hourly?.wes_p || 0;
      case "wes_c": return r.wes_hourly?.wes_c || 0;
      case "regime": return r.wes_hourly?.regime || "";
      case "dte": return r.dte || 0;
      default: return 0;
    }
  };

  const sorted = [...filtered].sort((a, b) => {
    const va = getVal(a, sortCol);
    const vb = getVal(b, sortCol);
    if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const SortHeader = ({ col, label }) => {
    const active = sortCol === col;
    return (
      <th className={`px-3 py-2.5 text-right cursor-pointer select-none hover:text-white transition-colors ${active ? "text-[#00FF88]" : "text-[#8A8A93]"}`} onClick={() => toggleSort(col)}>
        {label} {active ? (sortDir === "desc" ? "\u25BC" : "\u25B2") : ""}
      </th>
    );
  };

  // Iter115.3 — freshness dot helper.  Every row in a single scan
  // shares `scan.completed_at`; we derive a small coloured dot
  // that grades how stale the data is.  Operator gets a hover
  // tooltip explaining the meaning ("Fresh — 3m old · …").
  const freshnessForAgeMin = (ageMin) => {
    if (ageMin == null) return { color: "#3F3F47", label: "Unknown", state: "unknown" };
    if (ageMin < 5) return { color: "#00FF88", label: "Fresh", state: "fresh" };
    if (ageMin < 30) return { color: "#FFB800", label: "Aging", state: "aging" };
    return { color: "#FF3366", label: "Stale", state: "stale" };
  };
  const scanCompletedMs = scan?.completed_at
    ? new Date(scan.completed_at).getTime()
    : null;
  const scanAgeMin = scanCompletedMs != null
    ? Math.max(0, Math.floor((Date.now() - scanCompletedMs) / 60_000))
    : null;
  const freshness = freshnessForAgeMin(scanAgeMin);
  const freshnessTip = scanCompletedMs != null
    ? `${freshness.label} — ${scanAgeMin}m since scan completed (${new Date(scanCompletedMs).toLocaleTimeString()}).  ${
        freshness.state === "fresh" ? "Data current; safe to act."
        : freshness.state === "aging" ? "Consider rescanning before placing trades."
        : freshness.state === "stale" ? "Strongly rescan — option chain prices may have moved materially."
        : "Run a scan to populate freshness."
      }`
    : "Run a scan to populate freshness.";

  const isRunning = status?.status === "running";

  return (
    <div data-testid="market-scanner-page" className="animate-fade-in">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h2 className="text-3xl font-black tracking-tighter text-white font-['Chivo']">{t("market_scanner.title")}</h2>
          <p className="text-sm text-[#8A8A93] mt-1">{t("market_scanner.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Iter121.6 — Watchlist picker + manage. Same source of truth as
              MarketForge / Dashboard / StrategyScannerV2. Switching the
              active list here also updates it everywhere. */}
          <div className="min-w-[220px] max-w-[280px]" data-testid="scanner-page-wl-picker-wrap">
            <WatchlistPicker
              watchlists={watchlists}
              activeId={activeWatchlistId}
              onSwitch={switchActiveWatchlist}
              onManage={() => setShowManageWatchlists(true)}
              variant="expanded"
              testidPrefix="mscan-wl-picker"
            />
          </div>
          {isRunning && status?.progress_pct != null && (
            <div className="flex items-center gap-2">
              <div className="w-32 h-2 bg-[#26262B] rounded-full overflow-hidden"><div className="h-full bg-[#00FF88] transition-all rounded-full" style={{ width: `${status.progress_pct}%` }} /></div>
              <span className="text-xs font-mono text-[#00FF88]">{status.progress_pct}%</span>
            </div>
          )}
          {isRunning ? (
            // Iter121.7 — Cancel permitted for any authenticated user
            // since a viewer who Started may need to Cancel their own
            // run. Backend already allows both.
            <button onClick={cancelScan} data-testid="cancel-market-scan" className="text-xs px-4 py-2 bg-[#FF3366]/10 border border-[#FF3366]/30 text-[#FF3366] rounded-sm font-bold">{t("market_scanner.cancel_btn")}</button>
          ) : (
            // Iter121.7 — Start permitted for any authenticated user.
            // Was previously admin-only in the UI even though the
            // backend endpoint accepts get_current_user.
            <button onClick={startScan} disabled={starting} data-testid="start-market-scan" className="text-xs px-4 py-2 bg-[#00FF88] text-[#08080A] rounded-sm font-bold hover:bg-[#33FF9F] disabled:opacity-40">
              {starting ? "..." : t("market_scanner.start_btn")}
            </button>
          )}
        </div>
      </div>

      {/* Pre-scan filters */}
      <div className="mb-5">
        <button onClick={() => setShowFilters(!showFilters)} className="text-xs font-mono text-[#8A8A93] hover:text-white flex items-center gap-1 mb-2">
          <Settings size={12} /> {showFilters ? "Hide" : "Show"} Scan Filters
        </button>
        {showFilters && (
          <div className="bg-[#121216] border border-[#26262B] rounded-sm p-4 grid grid-cols-4 gap-3 mb-4">
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Price Min</label><input type="number" value={filters.price_min} onChange={e => setFilters({...filters, price_min: parseFloat(e.target.value) || 0})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Price Max</label><input type="number" value={filters.price_max} onChange={e => setFilters({...filters, price_max: parseFloat(e.target.value) || 0})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Min Avg Volume</label><input type="number" value={filters.min_avg_volume} onChange={e => setFilters({...filters, min_avg_volume: parseInt(e.target.value) || 0})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Min IVR</label><input type="number" placeholder="any" value={filters.min_ivr || ""} onChange={e => setFilters({...filters, min_ivr: e.target.value ? parseFloat(e.target.value) : null})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Min WES-P</label><input type="number" placeholder="any" value={filters.min_wes_p || ""} onChange={e => setFilters({...filters, min_wes_p: e.target.value ? parseFloat(e.target.value) : null})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Min WES-C</label><input type="number" placeholder="any" value={filters.min_wes_c || ""} onChange={e => setFilters({...filters, min_wes_c: e.target.value ? parseFloat(e.target.value) : null})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            <div><label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 block">Min CSP 75d ROI%</label><input type="number" placeholder="any" value={filters.min_csp_roi || ""} onChange={e => setFilters({...filters, min_csp_roi: e.target.value ? parseFloat(e.target.value) : null})} className="w-full bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1.5 text-xs font-mono" /></div>
            {/* Iter115.3 — Symbols override.  When non-empty, the scan
                bypasses the SP500+R2K universe AND the price / volume
                pre-filter.  Use it for off-index names the operator
                still wants to evaluate.  Comma, space, semicolon or
                newline separated; case-insensitive. */}
            <div className="col-span-4 lg:col-span-2">
              <label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 flex items-center gap-1.5">
                Symbols (override)
                <span
                  data-testid="symbols-override-help"
                  title="Comma, space or newline separated. When set, the scan SKIPS the SP500 + Russell 2000 universe AND the price/volume pre-filter, and runs full analysis on these tickers only. Use it for off-index names you've already vetted."
                  className="text-[#22D3CC] cursor-help"
                >ⓘ</span>
                <span className="flex-1" />
                {/* Iter121.6 — One-click seed from the active watchlist.
                    Replaces the override text with the active list's
                    symbols so the operator can scan their curated set
                    without hand-typing 30 tickers. */}
                <button
                  type="button"
                  onClick={seedFromActiveWatchlist}
                  disabled={seedingFromList || !activeWatchlistId}
                  data-testid="mscan-seed-from-watchlist"
                  title={activeWatchlist ? `Load ${activeWatchlist.name} (${activeWatchlist.item_count ?? 0}) into the override field` : "Load active watchlist into the override field"}
                  className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.1em] px-1.5 py-0.5 rounded-sm border border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[#22D3FF] hover:bg-[#22D3FF]/20 disabled:opacity-40"
                >
                  {seedingFromList ? <Loader2 size={9} className="animate-spin" /> : <RefreshCw size={9} />}
                  Use {activeWatchlist?.name || "watchlist"}{activeWatchlist?.item_count != null ? ` (${activeWatchlist.item_count})` : ""}
                </button>
              </label>
              <input
                type="text"
                data-testid="scan-symbols-override-input"
                placeholder="e.g. PLTR, RKLB, ASTS — leave empty for full universe"
                value={filters.symbols}
                onChange={e => setFilters({ ...filters, symbols: e.target.value.toUpperCase() })}
                className="w-full bg-[#08080A] border border-[#22D3CC]/30 focus:border-[#22D3CC] text-white rounded-sm px-2 py-1.5 text-xs font-mono"
              />
              {seedError && (
                <div className="text-[9px] font-mono text-[#FF3366] mt-0.5" data-testid="mscan-seed-error">{seedError}</div>
              )}
            </div>
            {/* Iter115.5 — "Why was X dropped?" diagnostic lookup.
                Tells the operator exactly why any ticker would or
                would not be analysed by the default universe sweep,
                with a one-click "Add to Symbols override" follow-up. */}
            <div className="col-span-4 lg:col-span-2 border-t border-[#26262B] pt-3 mt-1">
              <label className="text-[9px] font-bold uppercase text-[#8A8A93] mb-1 flex items-center gap-1.5">
                Why is a ticker not in my results?
                <span
                  data-testid="diagnose-help"
                  title="Type any ticker to see whether it's in the SP500+R2K universe and which pre-filter gate (price / volume / options) it passes or fails."
                  className="text-[#22D3CC] cursor-help"
                >ⓘ</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  data-testid="diagnose-input"
                  placeholder="e.g. BABA"
                  value={diagSymbol}
                  onChange={e => setDiagSymbol(e.target.value.toUpperCase())}
                  onKeyDown={e => { if (e.key === "Enter") runDiagnostic(); }}
                  className="flex-1 bg-[#08080A] border border-[#26262B] focus:border-[#22D3CC] text-white rounded-sm px-2 py-1.5 text-xs font-mono"
                />
                <button
                  type="button"
                  data-testid="diagnose-btn"
                  onClick={runDiagnostic}
                  disabled={diagLoading || !diagSymbol.trim()}
                  className="text-xs px-3 py-1.5 bg-[#22D3CC]/10 border border-[#22D3CC]/40 text-[#22D3CC] rounded-sm font-bold hover:bg-[#22D3CC]/20 disabled:opacity-40"
                >
                  {diagLoading ? <Loader2 size={12} className="animate-spin" /> : "Look up"}
                </button>
              </div>
              {diagResult && (
                <DiagnoseResultCard
                  data-testid="diagnose-result"
                  data={diagResult}
                  onAddToOverride={sendDiagToOverride}
                />
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scan status banner */}
      {isRunning && (
        <div className="bg-[#00FF88]/5 border border-[#00FF88]/20 rounded-sm px-4 py-3 mb-5 flex items-center gap-3">
          <Loader2 size={16} className="animate-spin text-[#00FF88]" />
          <span className="text-sm text-[#00FF88] font-mono">
            Scanning market... {status?.analyzed || 0} analyzed · {status?.pre_filtered || 0} candidates from {status?.universe_size || 0} universe
            {status?.universe_size > 0 && status?.pre_filtered != null && status.universe_size > status.pre_filtered && (
              <span
                data-testid="universe-prefilter-info"
                title={`Pre-filter dropped ${status.universe_size - status.pre_filtered} of ${status.universe_size} tickers — they failed at least one Wheel-strategy quality gate:\n  · Spot price ∈ [$${filters.price_min}, $${filters.price_max}]  (excludes penny stocks + mega-caps the Wheel ITM doctrine doesn't fit)\n  · 30-day average volume ≥ ${Number(filters.min_avg_volume).toLocaleString()}  (excludes illiquid names where the bid/ask is unreliable)\nAdjust these knobs in the Scan Filters above (or supply explicit Symbols to bypass them entirely).`}
                className="ml-1.5 text-[#22D3CC] cursor-help"
              >ⓘ</span>
            )}
          </span>
        </div>
      )}

      {/* Results */}
      {scan && scan.status === "completed" && (
        <>
          {/* Results meta */}
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs font-mono text-[#8A8A93]">
              Scanned {scan.analyzed} tickers from {scan.pre_filtered} candidates ({scan.universe_size} universe)
              {scan.universe_size > 0 && scan.pre_filtered != null && scan.universe_size > scan.pre_filtered && (
                <span
                  data-testid="universe-prefilter-info-done"
                  title={`Pre-filter dropped ${scan.universe_size - scan.pre_filtered} of ${scan.universe_size} tickers — they failed at least one Wheel-strategy quality gate:\n  · Spot price ∈ [$${(scan.filters || filters).price_min}, $${(scan.filters || filters).price_max}]  (excludes penny stocks + mega-caps the Wheel ITM doctrine doesn't fit)\n  · 30-day average volume ≥ ${Number((scan.filters || filters).min_avg_volume).toLocaleString()}  (excludes illiquid names where the bid/ask is unreliable)\nAdjust these knobs in the Scan Filters above (or supply explicit Symbols to bypass them entirely).`}
                  className="ml-1 text-[#22D3CC] cursor-help"
                >ⓘ</span>
              )}
              {scan.override_active && (
                <span
                  data-testid="override-active-badge"
                  title="This scan ran in OVERRIDE mode — the SP500+R2K universe AND the price/volume pre-filter were skipped. Only the operator-specified Symbols were analysed."
                  className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-sm border border-[#22D3CC]/40 text-[#22D3CC] bg-[#22D3CC]/10 text-[9px] uppercase tracking-[0.1em] cursor-help"
                >Override</span>
              )}
              <span className="ml-1">· {scan.opportunities} opportunities found</span>
              {scan.completed_at && <span className="ml-2 text-[#3F3F47]">{new Date(scan.completed_at).toLocaleString()}</span>}
            </div>
            {/* Client-side filters */}
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-[10px] font-mono text-[#8A8A93] cursor-pointer">
                <input type="checkbox" checked={fOnlyValid} onChange={e => setFOnlyValid(e.target.checked)} className="accent-[#00FF88]" /> Valid only
              </label>
              <input type="number" placeholder="Min ROI%" value={fMinRoi} onChange={e => setFMinRoi(e.target.value)} className="w-20 bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1 text-[10px] font-mono" />
              <input type="number" placeholder="Min WES-P" value={fMinWesP} onChange={e => setFMinWesP(e.target.value)} className="w-20 bg-[#08080A] border border-[#26262B] text-white rounded-sm px-2 py-1 text-[10px] font-mono" />
              <span className="text-[10px] font-mono text-[#8A8A93]">{sorted.length} showing</span>
              {/* Iter110 — Pin mode toggle + pinned counter. */}
              <button
                type="button"
                data-testid="market-scanner-pin-mode-toggle"
                onClick={() => setPinMode(v => !v)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-[0.14em] ${pinMode ? "border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800]" : "border-[#3F3F47] bg-[#0A0A0E] text-[#8A8A93] hover:border-[#FFB800]/60 hover:text-[#FFB800]"}`}
                title="Pin mode: send only the rows you've checked.  When OFF the Push button sends every visible (post-filter) ticker."
              >
                {pinMode ? "Pin mode · ON" : "Pin mode · OFF"} ({pinnedSymbols.size})
              </button>
              {pinMode && pinnedSymbols.size > 0 && (
                <button
                  type="button"
                  data-testid="market-scanner-clear-pins"
                  onClick={clearPins}
                  className="inline-flex items-center px-2 py-1 rounded-sm border border-[#26262B] text-[10px] font-mono text-[#8A8A93] uppercase tracking-[0.14em] hover:border-[#5F5F66] hover:text-[#E5E5E7]"
                  title="Clear the in-memory pin set.  Does not affect the scan results."
                >
                  Clear pins
                </button>
              )}
              {/* Iter109 — Push button: send the currently visible
                  (post-filter) tickers OR the pinned subset to the
                  Strategy Scanner.  Iter110 — pin-mode aware. */}
              {sorted.length > 0 && (
                <button
                  type="button"
                  data-testid="market-scanner-send-to-strategy-scanner"
                  onClick={() => {
                    const source = pinMode
                      ? sorted.filter(r => pinnedSymbols.has(
                          String(r.symbol || "").trim().toUpperCase()))
                      : sorted;
                    const syms = source
                      .map(r => String(r.symbol || "").trim().toUpperCase())
                      .filter(Boolean)
                      .filter((s, i, a) => a.indexOf(s) === i);
                    if (syms.length === 0) return;
                    navigate(`/strategy-scanner?symbols=${encodeURIComponent(syms.join(","))}`);
                  }}
                  disabled={pinMode && pinnedSymbols.size === 0}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-[0.14em] ${
                    pinMode && pinnedSymbols.size === 0
                      ? "border-[#26262B] bg-[#0A0A0E] text-[#3F3F47] cursor-not-allowed"
                      : "border-[#22D3FF]/40 bg-[#22D3FF]/10 text-[#22D3FF] hover:bg-[#22D3FF]/15"
                  }`}
                  title={pinMode
                    ? "Send ONLY the pinned tickers to the Strategy Scanner.  No execution, no journal write, no ranking — the operator still has to click Run."
                    : "Send the currently visible tickers to the Strategy Scanner.  No execution, no journal write, no ranking — the operator still has to click Run on the Strategy Scanner."}
                >
                  <Send size={11} />
                  {pinMode
                    ? `Send pinned (${pinnedSymbols.size})`
                    : `Send to Strategy Scanner (${sorted.length})`}
                </button>
              )}
            </div>
          </div>

          {/* Results table */}
          {filtered.length === 0 ? (
            <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-center"><Globe size={32} className="text-[#8A8A93] mx-auto mb-3" /><p className="text-[#8A8A93] text-sm">No tickers match your filters</p></div>
          ) : (
            <div className="bg-[#121216] border border-[#26262B] rounded-sm overflow-hidden">
              <table className="w-full text-[11px] font-mono">
                <thead className="sticky top-0 bg-[#0A0A0D] z-10">
                  <tr className="border-b border-[#26262B]">
                    {/* Iter110 — Pin column header.  Only rendered
                        in pin-mode to keep the table clean for the
                        default flow. */}
                    {pinMode && (
                      <th
                        data-testid="market-scanner-pin-col-header"
                        className="px-3 py-2.5 text-center text-[#FFB800] w-10"
                        title="Pin this row to send only the checked rows."
                      >Pin</th>
                    )}
                    <th className={`px-3 py-2.5 text-left cursor-pointer select-none hover:text-white ${sortCol === "symbol" ? "text-[#00FF88]" : "text-[#8A8A93]"}`} onClick={() => toggleSort("symbol")}>Ticker {sortCol === "symbol" ? (sortDir === "desc" ? "\u25BC" : "\u25B2") : ""}</th>
                    <th className={`px-3 py-2.5 text-left cursor-pointer select-none hover:text-white ${sortCol === "company" ? "text-[#00FF88]" : "text-[#8A8A93]"}`} onClick={() => toggleSort("company")}>Company {sortCol === "company" ? (sortDir === "desc" ? "\u25BC" : "\u25B2") : ""}</th>
                    <SortHeader col="spot" label="Spot" />
                    <SortHeader col="ivr" label="IVR" />
                    {/* Iter115.3 — Freshness-aware CSP 75 d ROI header.
                        Hover the ⓘ icon for the legend; the per-row
                        dot below grades how old the displayed value is. */}
                    <th
                      data-testid="csp-roi-header"
                      className={`px-3 py-2.5 text-right cursor-pointer select-none hover:text-white transition-colors ${sortCol === "csp_roi" ? "text-[#00FF88]" : "text-[#8A8A93]"}`}
                      onClick={() => toggleSort("csp_roi")}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        CSP 75d ROI
                        <span
                          data-testid="freshness-legend-icon"
                          onClick={e => e.stopPropagation()}
                          title={"Freshness dot legend:\n  🟢 Fresh — < 5 minutes since scan completed.\n  🟡 Aging — 5 to 30 minutes; consider rescanning.\n  🔴 Stale — > 30 minutes; rescan before acting."}
                          className="text-[#22D3CC] cursor-help text-[9px] normal-case tracking-normal"
                        >ⓘ</span>
                        {sortCol === "csp_roi" ? (sortDir === "desc" ? "\u25BC" : "\u25B2") : ""}
                      </span>
                    </th>
                    <SortHeader col="winner" label="Winner %" />
                    <SortHeader col="wes_p" label="WES-P" />
                    <SortHeader col="wes_c" label="WES-C" />
                    <th className="px-3 py-2.5 text-center text-[#8A8A93]">Eng</th>
                    <SortHeader col="regime" label="Regime" />
                    <SortHeader col="dte" label="EXP" />
                    <th className="px-3 py-2.5 text-center text-[#8A8A93]"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r, i) => {
                    const wh = r.wes_hourly;
                    const csp = r.csp_75d;
                    const winSc = r.winner === "ATM_CSP_ITM_CC" ? r.scenario_b : r.scenario_a;
                    const winPct = winSc?.income_pct;
                    const regCls = wh?.regime === "Bullish" ? "text-[#00FF88]" : wh?.regime === "Bearish" ? "text-[#FF3366]" : wh?.regime === "Mixed" ? "text-[#FFB800]" : "text-[#8A8A93]";
                    const symU = String(r.symbol || "").trim().toUpperCase();
                    const isPinned = pinnedSymbols.has(symU);
                    return (
                      <tr key={i} className={`border-b border-[#1A1A20] hover:bg-[#1A1A20]/50 cursor-pointer ${pinMode && isPinned ? "bg-[#FFB800]/5" : ""}`} onClick={() => quickAnalyze(r.symbol)}>
                        {/* Iter110 — Per-row pin checkbox cell. */}
                        {pinMode && (
                          <td
                            className="px-3 py-2 text-center"
                            onClick={e => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              data-testid={`market-scanner-pin-${symU}`}
                              checked={isPinned}
                              onChange={() => togglePin(symU)}
                              className="accent-[#FFB800] cursor-pointer"
                              title={isPinned ? "Unpin this row" : "Pin this row to send only the checked rows"}
                            />
                          </td>
                        )}
                        <td className="px-3 py-2"><div className="flex items-center gap-2"><TickerLogo symbol={r.symbol} /><span className="font-bold text-white text-xs">{r.symbol}</span></div></td>
                        <td className="px-3 py-2 text-left text-[#8A8A93] text-[10px] max-w-[140px] truncate">{r.company || "—"}</td>
                        <td className="px-3 py-2 text-right text-white">${r.spot?.toFixed(2)}</td>
                        <td className={`px-3 py-2 text-right font-bold ${r.ivr >= 50 ? "text-[#00FF88]" : r.ivr >= 30 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{r.ivr ?? "—"}</td>
                        <td className={`px-3 py-2 text-right font-bold ${csp?.roi >= 3 ? "text-[#00FF88]" : csp?.roi >= 2 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>
                          <span className="inline-flex items-center justify-end gap-1.5">
                            <span
                              data-testid={`freshness-dot-${r.symbol}`}
                              data-freshness={freshness.state}
                              title={freshnessTip}
                              className="w-2 h-2 rounded-full cursor-help shadow-[0_0_4px_currentColor]"
                              style={{ backgroundColor: freshness.color, color: freshness.color }}
                            />
                            {csp?.roi ?? "—"}%
                          </span>
                        </td>
                        <td className={`px-3 py-2 text-right font-bold ${winPct >= 3 ? "text-[#00FF88]" : "text-white"}`}>{winPct != null ? winPct.toFixed(1) + "%" : "—"}</td>
                        <td className={`px-3 py-2 text-center font-bold ${wh?.wes_p >= 7 ? "text-[#00FF88]" : wh?.wes_p >= 5 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{wh?.wes_p ?? "—"}</td>
                        <td className={`px-3 py-2 text-center font-bold ${wh?.wes_c >= 7 ? "text-[#00FF88]" : wh?.wes_c >= 5 ? "text-[#FFB800]" : "text-[#8A8A93]"}`}>{wh?.wes_c ?? "—"}</td>
                        <td className="px-3 py-2 text-center">
                          {/* Iter42 — Per-row PARITY/LGY/ERR badge. Rows that are
                              not parity-valid are visually distinguished so the
                              user cannot mistake them for an official WES read. */}
                          <WesEngineBadge
                            engine={wh?.wes_engine || r.wes_engine}
                            parityValid={wh?.wes_parity_valid ?? r.wes_parity_valid}
                            freshnessState={wh?.freshness_state}
                            scannedAt={wh?.scanned_at}
                            source={wh?.source}
                            testid={`mscan-engine-${r.symbol}`}
                          />
                        </td>
                        <td className={`px-3 py-2 text-center font-bold ${regCls}`}>{wh ? `${wh.regime_signs} ${wh.regime}` : "—"}</td>
                        <td className="px-3 py-2 text-right text-[#8A8A93]">{r.expiration} ({r.dte}d)</td>
                        <td className="px-3 py-2 text-center"><button onClick={e => { e.stopPropagation(); quickAnalyze(r.symbol); }} className="text-[10px] px-2.5 py-1 bg-[#007AFF]/10 border border-[#007AFF]/30 text-[#007AFF] rounded-sm font-bold hover:bg-[#007AFF]/20 transition-colors">Analyze</button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Empty state */}
      {!scan && !isRunning && !loading && (
        <div className="bg-[#121216] border border-[#26262B] rounded-sm p-12 text-center">
          <Globe size={40} className="text-[#8A8A93] mx-auto mb-3" />
          <p className="text-[#8A8A93] text-sm mb-4">No market scan results yet. Configure filters and click "Scan Market" to find opportunities across 500+ tickers.</p>
        </div>
      )}

      {/* Quick Analyze Modal — Iter115.1 full-width + live spot stream.
          Iter115.4: portaled to document.body so the fixed-position
          overlay escapes any ancestor that creates a containing
          block (the sidebar was clipping the left edge otherwise). */}
      {quickTicker && createPortal((
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => { setQuickTicker(null); setQuickData(null); setQuickRefreshedAt(null); }}>
          <div className="w-full max-w-[1600px] max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <span data-testid="quick-analyze-title" className="text-sm font-mono text-[#8A8A93]">Quick Analyze — <span className="text-white font-bold">{quickTicker}</span></span>
                <QuickLiveSpot symbol={quickTicker} />
                <QuickRefreshBadge
                  refreshedAt={quickRefreshedAt}
                  refreshing={quickRefreshing}
                  loading={quickLoading}
                  onRefresh={() => quickRefreshAnalysis(quickTicker)}
                />
              </div>
              <button data-testid="quick-analyze-close" onClick={() => { setQuickTicker(null); setQuickData(null); setQuickRefreshedAt(null); }} className="text-[#8A8A93] hover:text-white"><X size={18} /></button>
            </div>
            {quickLoading && <div className="bg-[#121216] border border-[#26262B] rounded-sm p-12 text-center"><Loader2 size={28} className="animate-spin text-[#00FF88] mx-auto mb-3" /><p className="text-[#8A8A93] text-sm">Running full analysis on {quickTicker}...</p></div>}
            {quickData && <TickerCard r={quickData} showTradeBtn={true} />}
            {!quickLoading && !quickData && <div className="bg-[#121216] border border-[#26262B] rounded-sm p-8 text-center text-[#FF3366] text-sm">No data returned for {quickTicker}</div>}
          </div>
        </div>
      ), document.body)}

      {/* Iter115.5 — "Already running" confirm modal.  Surfaces the
          previously-silent server response so the operator can
          (a) wait for the current scan to finish, or
          (b) cancel it and immediately re-launch with their own
              payload (including any Symbols override they just typed). */}
      {alreadyRunningInfo && createPortal((
        <div data-testid="already-running-modal" className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[110] flex items-center justify-center p-4" onClick={() => setAlreadyRunningInfo(null)}>
          <div className="w-full max-w-md bg-[#121216] border border-[#FFB800]/40 rounded-sm p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Loader2 size={16} className="text-[#FFB800] animate-spin" />
              <h3 className="text-sm font-bold uppercase tracking-[0.12em] text-[#FFB800]">A scan is already running</h3>
            </div>
            <p className="text-xs text-[#E0E0E0] leading-relaxed mb-1">
              The previous scan kicked off at{" "}
              <span className="font-mono text-white">{alreadyRunningInfo.started_at ? new Date(alreadyRunningInfo.started_at).toLocaleTimeString() : "—"}</span>{" "}
              and is still in progress.
            </p>
            <p className="text-xs text-[#8A8A93] mb-4">
              Your request was for: <span className="text-white font-mono">{alreadyRunningInfo.payload_summary}</span>.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                data-testid="already-running-wait"
                onClick={() => setAlreadyRunningInfo(null)}
                className="text-xs px-3 py-2 border border-[#26262B] text-[#8A8A93] rounded-sm hover:text-white"
              >Wait for current</button>
              <button
                type="button"
                data-testid="already-running-replace"
                onClick={async () => {
                  setAlreadyRunningInfo(null);
                  await startScan({ replace: true });
                }}
                className="text-xs px-3 py-2 bg-[#FFB800] text-[#08080A] rounded-sm font-bold hover:bg-[#FFD040]"
              >Cancel &amp; run mine</button>
            </div>
          </div>
        </div>
      ), document.body)}
      {/* Iter121.6 — Manage watchlists modal. Same shared component
          used across MarketForge / Dashboard / StrategyScannerV2. */}
      {showManageWatchlists && (
        <ManageWatchlistsModal
          watchlists={watchlists}
          activeId={activeWatchlistId}
          onClose={() => setShowManageWatchlists(false)}
          onRefresh={() => refetchWatchlists(activeWatchlistId)}
          onSwitchActive={switchActiveWatchlist}
          testidPrefix="mscan-wl-manage"
        />
      )}
    </div>
  );
}

// Iter115.5 — "Why was X dropped?" result card.
const DiagTick = ({ ok }) => (
  <span className={ok === true ? "text-[#00FF88]" : ok === false ? "text-[#FF3366]" : "text-[#5F5F66]"}>
    {ok === true ? "PASS" : ok === false ? "FAIL" : "—"}
  </span>
);
function DiagnoseResultCard({ data, onAddToOverride }) {
  if (!data) return null;
  const COLORS = {
    would_pass:        { bd: "border-[#00FF88]/40", bg: "bg-[#00FF88]/5",  fg: "text-[#00FF88]" },
    off_universe:      { bd: "border-[#22D3CC]/40", bg: "bg-[#22D3CC]/5",  fg: "text-[#22D3CC]" },
    fails_price_gate:  { bd: "border-[#FFB800]/40", bg: "bg-[#FFB800]/5",  fg: "text-[#FFB800]" },
    fails_volume_gate: { bd: "border-[#FFB800]/40", bg: "bg-[#FFB800]/5",  fg: "text-[#FFB800]" },
    no_options:        { bd: "border-[#FF3366]/40", bg: "bg-[#FF3366]/5",  fg: "text-[#FF3366]" },
    no_quote:          { bd: "border-[#FF3366]/40", bg: "bg-[#FF3366]/5",  fg: "text-[#FF3366]" },
    error:             { bd: "border-[#FF3366]/40", bg: "bg-[#FF3366]/5",  fg: "text-[#FF3366]" },
  };
  const sty = COLORS[data.verdict] || COLORS.error;
  // Whether the operator can solve this verdict by adding the
  // symbol to the Symbols override field.
  const fixable = ["off_universe", "fails_price_gate", "fails_volume_gate"].includes(data.verdict);
  return (
    <div data-testid="diagnose-result" data-verdict={data.verdict} className={`mt-2 rounded-sm border ${sty.bd} ${sty.bg} p-3 text-[11px] font-mono`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-white font-bold">{data.symbol}</span>
          {data.description && <span className="text-[#8A8A93]">· {data.description}</span>}
        </div>
        <span className={`uppercase tracking-[0.14em] font-bold ${sty.fg}`}>{(data.verdict || "").replace(/_/g, " ")}</span>
      </div>
      <p className="text-[#E0E0E0] leading-relaxed mb-3">{data.reason}</p>
      {data.quote_ok !== false && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">Spot</span>
            <span className="text-white">{data.last != null ? `$${Number(data.last).toFixed(2)}` : "—"}</span>
          </div>
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">Avg Vol</span>
            <span className="text-white">{data.avg_volume != null ? Number(data.avg_volume).toLocaleString() : "—"}</span>
          </div>
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">In universe</span>
            <DiagTick ok={data.gates?.in_universe} />
          </div>
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">Has options</span>
            <DiagTick ok={data.has_options} />
          </div>
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">Price gate</span>
            <DiagTick ok={data.gates?.price} />
          </div>
          <div className="flex justify-between border border-[#26262B] rounded-sm px-2 py-1">
            <span className="text-[#8A8A93]">Volume gate</span>
            <DiagTick ok={data.gates?.volume} />
          </div>
        </div>
      )}
      {fixable && (
        <button
          type="button"
          data-testid="diagnose-add-to-override"
          onClick={onAddToOverride}
          className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 rounded-sm border border-[#22D3CC]/40 text-[#22D3CC] bg-[#22D3CC]/10 hover:bg-[#22D3CC]/20 font-bold"
        >
          + Add to Symbols override
        </button>
      )}
    </div>
  );
}

// Iter115.2 — re-analysis status badge.  Shows:
//   - `ANALYSIS · running…` (amber, spinning) when refreshing.
//   - `ANALYSIS · 12 s ago · ↻` (cyan) when fresh.  Click the
//     ↻ to force-refresh.  Auto-refresh interval is 20 s.
function QuickRefreshBadge({ refreshedAt, refreshing, loading, onRefresh }) {
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1_000);
    return () => clearInterval(iv);
  }, []);
  if (loading) return null;
  const ago = (() => {
    if (!refreshedAt) return null;
    const ms = Math.max(0, Date.now() - new Date(refreshedAt).getTime());
    const s = Math.floor(ms / 1000);
    if (s < 1) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m}m ago`;
  })();
  if (refreshing) {
    return (
      <span data-testid="quick-refresh-badge-running" className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border border-[#FFB800]/40 text-[#FFB800] bg-[#FFB800]/10 inline-flex items-center gap-1.5">
        <RefreshCw size={10} className="animate-spin" />
        ANALYSIS · running…
      </span>
    );
  }
  if (!ago) return null;
  return (
    <button
      type="button"
      data-testid="quick-refresh-badge"
      onClick={onRefresh}
      title="Force-refresh analysis"
      className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border border-[#22D3CC]/40 text-[#22D3CC] bg-[#22D3CC]/10 inline-flex items-center gap-1.5 hover:bg-[#22D3CC]/20 transition-colors"
    >
      ANALYSIS · <span className="text-[#8A8A93] font-normal" data-testid="quick-refresh-badge-when">{ago}</span>
      <RefreshCw size={10} />
    </button>
  );
}

// Iter115.1 — Live spot streaming pill for the Quick Analyze modal.
// Polls `/api/quote/live/{symbol}` every 10s and shows
// `LIVE $279.31 · just now` (or `STALE` if the latest poll failed).
// Read-only: zero side effects on the modal's analysis data.
function QuickLiveSpot({ symbol }) {
  const [spot, setSpot] = React.useState(null);
  const [fetchedAt, setFetchedAt] = React.useState(null);
  const [err, setErr] = React.useState(false);
  const [tick, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/quote/live/${encodeURIComponent(symbol)}`);
        if (cancelled) return;
        setSpot(r?.data?.last ?? null);
        setFetchedAt(r?.data?.fetched_at || new Date().toISOString());
        setErr(false);
      } catch {
        if (!cancelled) setErr(true);
      }
    };
    fetchOnce();
    const iv = setInterval(fetchOnce, 10_000);
    // Re-render every 5s so the "X s ago" relative label stays fresh.
    const tickIv = setInterval(() => setTick(t => t + 1), 5_000);
    return () => { cancelled = true; clearInterval(iv); clearInterval(tickIv); };
  }, [symbol]);
  const ago = (() => {
    if (!fetchedAt) return null;
    const ms = Math.max(0, Date.now() - new Date(fetchedAt).getTime());
    const s = Math.floor(ms / 1000);
    if (s < 10) return "just now";
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    return `${m}m ago`;
  })();
  if (err && spot == null) {
    return (
      <span data-testid="quick-live-spot-error" className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border border-[#FF3366]/40 text-[#FF3366] bg-[#FF3366]/5">
        LIVE OFF
      </span>
    );
  }
  if (spot == null) {
    return (
      <span data-testid="quick-live-spot-loading" className="text-[10px] px-2 py-0.5 rounded-sm font-mono border border-[#3F3F47] text-[#8A8A93]">
        LIVE …
      </span>
    );
  }
  return (
    <span data-testid="quick-live-spot" className="text-[10px] px-2 py-0.5 rounded-sm font-mono font-bold border border-[#00FF88]/40 text-[#00FF88] bg-[#00FF88]/10" title={`Source: ${tick >= 0 ? "broker" : ""}`}>
      LIVE ${Number(spot).toFixed(2)} · <span className="text-[#8A8A93] font-normal">{ago}</span>
    </span>
  );
}


